import { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { digObject } from 'functions/utilities'
import getImageResolution from 'functions/getImageResolution'

import useSetState from 'hooks/useSetState'
import useThunkDispatch from 'hooks/useThunkDispatch'

import useLatestEntity from 'agency/hooks/useLatestEntity'

import * as imageActions from 'agency/redux/modules/image'

// IMPORTANT - Make sure you have added a PageContext.Provider
// to the page that contains this hook

import PageContext from 'contexts/pageContext'

const deleteImage = (image, dispatch, requestOptions) => {
  const { deleteImage: deleteFn } = imageActions
  return dispatch(deleteFn(image, requestOptions))
}

const editImage = (image, dispatch, showManageImageModal) => {
  const { updateImage: updateFn } = imageActions

  return new Promise((resolve, reject) => {
    if (showManageImageModal){
      const payload = {
        callbacks: {
          updateImage: (imageParams, requestOptions) => dispatch(updateFn(imageParams, requestOptions)),
        },
        image,
      }

      showManageImageModal(payload)

      return resolve({ success: true, result: payload })
    }

    return reject(new Error('showManageImageModal not defined in PageContext callbacks'))
  })
}

const selectImage = (image, dispatch) => {
  const { selectImageId: selectFn } = imageActions
  return dispatch(selectFn(image.id))
}

const updateImage = (imageParams, dispatch, requestOptions) => {
  const { updateImage: updateFn } = imageActions
  return dispatch(updateFn(imageParams, requestOptions))
}

const updateTags = (updatedTags, image, dispatch, setState) => {
  const updatedImage = {
    id: image.id,
    tags: updatedTags,
  }

  setState({ selectedTags: updatedTags })

  return updateImage(updatedImage, dispatch)
}

export function useRelations(image){
  const {
    imageable_id,
    imageable_type,
  } = image

  const entities = useSelector(reduxState => reduxState.entities)

  const {
    agencies,
    campaigns,
  } = entities

  const campaign = imageable_type === 'Campaign' && campaigns[imageable_id] ? campaigns[imageable_id] : {}
  const agency = agencies[campaign.agency_id] || {}

  return {
    agency,
    campaign,
    entities,
  }
}

const defaultState = {
  selectedTags: [],
}

function useImage(initEntity = {}){
  const { entity: image } = useLatestEntity(initEntity, 'images')
  const { image_type } = image
  const tags = digObject(image, 'data.tags', [])

  const [state, setState] = useSetState(defaultState)
  const { selectedTags } = state

  useEffect(() => {
    if (tags.length){
      setState({ selectedTags: tags })
    }
  }, [image.id, tags.length])

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const {
    showManageImageModal,
  } = callbacks || {}

  const { deleting, updating } = useSelector(reduxState => reduxState.images)

  const relations = useRelations(image)
  const { agency, campaign, entities } = relations

  const createdDate = digObject(image, 'dates.created.date_time')
  const createdDateObj = moment(createdDate, 'DD/MM/YYYY HH:mm')

  const resolution = getImageResolution(image)
  const isPlan = image_type === 'plan'

  return {
    agency,
    callbacks: {
      deleteImage: requestOptions => deleteImage(image, dispatch, requestOptions),
      editImage: () => editImage(image, dispatch, showManageImageModal),
      selectImage: () => selectImage(image, dispatch),
      setState,
      updateImage: (updatedParams, requestOptions) => updateImage(updatedParams, dispatch, requestOptions),
      updateTags: updatedTags => updateTags(updatedTags, image, dispatch, setState),
    },
    campaign,
    createdDateObj,
    deleting,
    entities,
    image,
    isPlan,
    resolution,
    selectedTags,
    updating,
  }
}

export default useImage
